import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { analytics } from "../../helpers/analytics";
import {
  trackFunnelStart,
  trackPaywallView,
  trackCheckoutStart,
  trackSubscriptionPurchase,
  trackStartTrial,
  trackStartFunnelCustomEvent,
} from "../../helpers/metaPixel";
import { formatAmount } from "../../helpers/shared";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { SignIn } from "../../components/SignIn";
import { UserContext } from "../../context/userContext";
import { useExitIntent, ExitOfferModal } from "./ExitOffer";
import { selectFunnelPrices, FunnelPrices } from "./prices";
import {
  QuizStages,
  QuizAnswers,
  packFor,
  testimonialFor,
} from "./QuizStages";

import appIcon from "../../assets/images/funnel/app-icon.png";
import packGridImg from "../../assets/images/funnel/grid-items-paywall.webp";
import mayaAvatar from "../../assets/images/funnel/review-maya.webp";
import kenjiAvatar from "../../assets/images/funnel/review-kenji.webp";
import sofiaAvatar from "../../assets/images/funnel/review-sofia.webp";

/**
 * Funnel v2 — single-link "montage showcase" concept.
 * Flow: looping technique reel → full-library payoff → hard paywall → Stripe.
 * No quiz: the reel already told us the intent, so we match it at the category
 * level (lineart → shading → fill) instead of asking. Fires the same shared
 * Funnel events as the classic funnel so both arms compare on the same metrics;
 * the arm is distinguished by the funnel_v2_variant super property (set by the
 * wrapper in index.tsx before this mounts).
 */

// Same localStorage keys as the classic funnel so a person keeps one identity
// and their profile/email survive if they land in either arm.
const PROFILE_ID_KEY = "funnel_external_profile_id";
const UTM_KEY = "funnel_entry_params";
const EMAIL_KEY = "funnel_email";

type Phase = "montage" | "quiz" | "payoff" | "paywall" | "success";

// The looping technique reel (lineart → shading → paint), 4:5 to match the
// hero container. Clean export — no IG/Manychat overlay text. Optimized to
// ~280KB (720x900, audio stripped, faststart) so it doesn't slow first paint.
const SHOWCASE_VIDEO_SRC = "/funnel/showcase.mp4";
const SHOWCASE_POSTER_SRC = "/funnel/showcase-poster.jpg";
const SCENES = ["Lineart", "Shading", "Color fill"];
// Each technique runs ~4s in the reel. The caption is driven off the video's
// own currentTime (not a separate timer) so it stays in sync on every loop.
const SECTION_SECONDS = 4;

/**
 * Everything the first screen claims about the app, in one place, taken from
 * the App Store listing (id1559271447) on 4 Aug 2026 unless noted. Kept as
 * constants rather than typed into the markup so there is one thing to
 * re-check when the listing moves.
 *
 * `rating` is set by the owner, not read from the listing: the US storefront
 * showed 4.6 on 4 Aug. Ratings differ by storefront and drift daily, so treat
 * this as a figure to re-confirm rather than one to trust indefinitely.
 */
const APP_FACTS = {
  rating: "4.7",
  ratings: "16K",
  artists: "1M+",
  countries: "130+",
  brushes: "10,000+",
  founded: "2024",
  developer: "Tools Products FZ-LLC",
  category: "Graphics & Design",
  lastUpdate: "26 July 2026",
  version: "2.31",
};

/** What the subscription actually contains. Brushes first and biggest, because
 *  that is what the reel promised and what every buyer came for; the rest is
 *  there to make the price feel covered, not to compete for attention. */
const WHATS_INSIDE = [
  { icon: "🖌", label: "Brushes", detail: "10,000+ across every style", hero: true },
  { icon: "🔤", label: "Fonts", detail: "For lettering & covers" },
  { icon: "🎨", label: "Palettes", detail: "Ready-made colour sets" },
  { icon: "✨", label: "Stickers & textures", detail: "Drop straight onto canvas" },
  { icon: "🧊", label: "3D models", detail: "Pose and trace from" },
  { icon: "🎓", label: "Academy", detail: "Courses & workflow guides" },
];

const REVIEWS = [
  {
    name: "Maya R.",
    detail: "Portrait artist",
    text: "The brushes completely changed my portraits. I finally stopped fighting the defaults.",
    image: mayaAvatar,
  },
  {
    name: "Kenji T.",
    detail: "Anime & manga",
    text: "Best line set I've found — my linework looks clean straight away.",
    image: kenjiAvatar,
  },
  {
    name: "Sofia L.",
    detail: "Lettering",
    text: "Sold my first commission two weeks after getting the library.",
    image: sofiaAvatar,
  },
];

const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getProfileId = (): string => {
  try {
    const existing = localStorage.getItem(PROFILE_ID_KEY);
    if (existing) return existing;
    const id = generateUUID();
    localStorage.setItem(PROFILE_ID_KEY, id);
    return id;
  } catch {
    return generateUUID();
  }
};

const storeEntryParams = (): Record<string, string> => {
  try {
    const params = new URLSearchParams(window.location.search);
    const keep: Record<string, string> = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid", "ad_id", "adset_id", "campaign_id"].forEach(
      (k) => {
        const v = params.get(k);
        if (v) keep[k] = v;
      }
    );
    if (Object.keys(keep).length) {
      localStorage.setItem(UTM_KEY, JSON.stringify(keep));
    }
    return keep;
  } catch {
    return {};
  }
};

const getEntryParams = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(UTM_KEY) || "{}");
  } catch {
    return {};
  }
};

const getStoredEmail = (): string => {
  try {
    return localStorage.getItem(EMAIL_KEY) || "";
  } catch {
    return "";
  }
};

// Stripe returns to /subscription-activated for immediate purchases and
// /trial-activated when a free trial started. Both are the success screen; the
// trial path logs a trial start ($0 commitment) instead of a real purchase.
// The countdown offer. It is worth stating plainly: while this runs, checkout
// really does apply the discount coupon, and when it expires the visitor really
// does pay full price. A timer counting down to nothing is a lie the visitor can
// catch by reloading, and our own App Store reviews already accuse us of
// promising more than we hand over — that is not a complaint to feed.
const OFFER_MINUTES = 15;
const OFFER_DEADLINE_KEY = "funnel_offer_deadline";

const getOfferDeadline = (): number => {
  try {
    const stored = Number(localStorage.getItem(OFFER_DEADLINE_KEY));
    if (stored && stored > Date.now()) return stored;
    const deadline = Date.now() + OFFER_MINUTES * 60 * 1000;
    localStorage.setItem(OFFER_DEADLINE_KEY, String(deadline));
    return deadline;
  } catch {
    return Date.now() + OFFER_MINUTES * 60 * 1000;
  }
};

const isSuccessPath = () =>
  typeof window !== "undefined" &&
  (window.location.pathname.includes("subscription-activated") ||
    window.location.pathname.includes("trial-activated"));

const isTrialSuccessPath = () =>
  typeof window !== "undefined" &&
  window.location.pathname.includes("trial-activated");

// Loaded once for the whole module, and only when a key exists. Without one the
// embedded form cannot mount, so the funnel keeps using the hosted redirect
// rather than showing an empty box where the card fields should be.
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

export const MontageFunnel = ({
  exitOfferEnabled = false,
  withQuiz = false,
  timerOffer = false,
  styleEmoji = false,
  offerIntroArm = "control",
  embeddedCheckout = false,
}: {
  exitOfferEnabled?: boolean;
  /** Insert the warm-up stages (questions → building → email) after the reel. */
  withQuiz?: boolean;
  /** Draw the first quiz question as emoji tiles rather than eight photographs. */
  styleEmoji?: boolean;
  /**
   * Which countdown announcement the visitor gets.
   *
   * `control` — the original modal, over question one.
   * `test`    — the loud one (clock as the subject, shown expiring), also over
   *             question one, repeated once the pack exists.
   * `late`    — the loud one, but held back until the email step. A quarter to
   *             a third of everyone who sees this screen never gets past it,
   *             and at question one there is nothing to lose yet: no pack, no
   *             answers, nothing earned. Held until the visitor has something.
   *
   * The countdown strip under the progress bar shows in every arm — what moves
   * is the full-screen interruption, not the offer.
   */
  offerIntroArm?: "control" | "test" | "late";
  /**
   * Take the payment form onto our own page instead of sending the buyer to
   * checkout.stripe.com. Ninety people a day reach that redirect and a handful
   * come back; leaving the domain is the most-cited reason a checkout dies.
   */
  embeddedCheckout?: boolean;
  /** Show a counting-down coupon that genuinely discounts checkout while it runs. */
  timerOffer?: boolean;
}) => {
  const [phase, setPhase] = useState<Phase>(() =>
    isSuccessPath() ? "success" : "montage"
  );
  const [prices, setPrices] = useState<FunnelPrices>({});
  const [selectedPlan, setSelectedPlan] = useState<"annual" | "weekly">("annual");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [isAuthOpen, setAuthOpen] = useState(false);
  const [exitOfferOpen, setExitOfferOpen] = useState(false);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({});
  const [quizEmail, setQuizEmail] = useState("");
  const [quizProgress, setQuizProgress] = useState(0);
  const [offerSecondsLeft, setOfferSecondsLeft] = useState<number | null>(null);
  const [offerIntroOpen, setOfferIntroOpen] = useState(false);
  const [offerExpiredOpen, setOfferExpiredOpen] = useState(false);
  const [embeddedSecret, setEmbeddedSecret] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const profileId = useRef(getProfileId());
  const storedEmail = quizEmail || getStoredEmail();

  // With the warm-up on, the payoff speaks to the style the visitor picked
  // instead of showing everyone the same library shelf.
  const pack = packFor(answers.style);
  const testimonial = testimonialFor(answers.style);
  const personalized = withQuiz && Boolean(answers.style);

  // Funnel entry — mirror the classic funnel: identify always, but only log a
  // fresh entry when this isn't a Stripe return (that URL carries no UTMs).
  useEffect(() => {
    const entryParams = storeEntryParams();
    const returningFromCheckout = isSuccessPath();
    analytics.identify(profileId.current, {
      funnel: "start_brush_quiz",
      ...(returningFromCheckout ? getEntryParams() : entryParams),
    });
    if (returningFromCheckout) return;
    analytics.track("Funnel Started", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
      ...entryParams,
    });
    trackFunnelStart({ external_profile_id: profileId.current, ...entryParams });
  }, []);

  // Drive the caption off the video's own playback so it matches the technique
  // on screen (each section ~4s) and re-syncs automatically on every loop.
  useEffect(() => {
    if (phase !== "montage") return;
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => {
      const idx = Math.min(
        Math.floor(video.currentTime / SECTION_SECONDS),
        SCENES.length - 1
      );
      setSceneIdx((cur) => (cur === idx ? cur : idx));
    };
    video.addEventListener("timeupdate", onTime);
    onTime();
    return () => video.removeEventListener("timeupdate", onTime);
  }, [phase]);

  // Stripe prices for the in-house paywall.
  useEffect(() => {
    fetch("/.netlify/functions/get-products")
      .then((res) => res.json())
      .then((products) => setPrices(selectFunnelPrices(products)))
      .catch(() => {
        analytics.track("Funnel Prices Failed", { funnel: "start_brush_quiz" });
      });
  }, []);

  useEffect(() => {
    if (phase !== "paywall") return;
    trackPaywallView({ source: "start_brush_quiz" });
    trackStartFunnelCustomEvent("StartFunnelPaywallViewed", {
      external_profile_id: profileId.current,
    });
    analytics.track("Funnel Paywall Viewed", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
    });
  }, [phase]);

  const successHandled = useRef(false);
  useEffect(() => {
    if (phase !== "success" || successHandled.current) return;
    successHandled.current = true;

    const trialActivated = isTrialSuccessPath();

    // Comparable across arms: made it through Stripe. Primary trial-experiment
    // metric — trial starts and real purchases both count.
    analytics.track("Funnel Checkout Completed", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
      plan: selectedPlan,
      is_trial: trialActivated,
    });

    if (trialActivated) {
      analytics.track("Funnel Trial Started", {
        funnel: "start_brush_quiz",
        external_profile_id: profileId.current,
        plan: selectedPlan,
      });
    } else {
      analytics.track("Funnel Purchase Completed", {
        funnel: "start_brush_quiz",
        external_profile_id: profileId.current,
        plan: selectedPlan,
      });
    }

    let alreadyTracked = false;
    try {
      alreadyTracked = sessionStorage.getItem("funnel_purchase_tracked") === "1";
      sessionStorage.setItem("funnel_purchase_tracked", "1");
    } catch {}

    if (!alreadyTracked) {
      let amount: string | null = null;
      try {
        amount = localStorage.getItem("last_purchase");
      } catch {}
      if (trialActivated) {
        // Standard StartTrial (optimizable) + our granular custom event — the
        // trial mirror of pay-now's Purchase. The real Purchase fires only on the
        // actual charge (server-side CAPI on invoice.paid, fast-follow), so ads
        // are never told "purchased" for a $0 trial.
        trackStartTrial(amount);
        trackStartFunnelCustomEvent("StartFunnelTrialStarted", {
          external_profile_id: profileId.current,
          plan: selectedPlan,
          value: amount ? Number(amount) : undefined,
          currency: "USD",
        });
      } else {
        trackStartFunnelCustomEvent("StartFunnelPurchaseCompleted", {
          external_profile_id: profileId.current,
          plan: selectedPlan,
          value: amount ? Number(amount) : undefined,
          currency: "USD",
        });
        trackSubscriptionPurchase(amount, "funnel_subscription");
      }
    }

    setAuthOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const annualSavings = useMemo(() => {
    if (!prices.weekly?.amount || !prices.annual?.amount) return null;
    const savings = Math.round(
      (1 - prices.annual.amount / (prices.weekly.amount * 52)) * 100
    );
    return savings > 0 ? savings : null;
  }, [prices]);

  // Runs from the moment the visitor lands, not from the paywall: an offer that
  // only starts counting once you reach the price is a countdown the visitor has
  // never had a chance to lose, which is the tell that it isn't real.
  useEffect(() => {
    if (!timerOffer || phase === "success") return;
    const deadline = getOfferDeadline();
    const tick = () =>
      setOfferSecondsLeft(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timerOffer, phase]);

  // The coupon is only real when there are two annual prices to move between:
  // a list price we genuinely charge once the clock stops, and the lower one it
  // grants while the clock runs. Without the dearer price there is nothing to
  // discount, and a countdown ticking down to no change is decoration — so it
  // does not appear at all until Stripe returns both.
  const offerBacked = Boolean(
    prices.annual && prices.annualFull && prices.annualFull.amount > prices.annual.amount
  );
  const offerLive =
    timerOffer && offerBacked && offerSecondsLeft !== null && offerSecondsLeft > 0;
  // A deadline nobody ever sees pass is indistinguishable from a decoration.
  // When the clock runs out the offer is shown expiring rather than quietly
  // removed — that is what makes the next countdown mean anything.
  const offerExpired =
    timerOffer && offerBacked && offerSecondsLeft !== null && offerSecondsLeft <= 0;
  // Both non-control arms use the louder wording and the shown expiry; they
  // differ only in when the announcement interrupts.
  const loudIntro = offerIntroArm !== "control";

  const seen = (key: string) => {
    try {
      if (sessionStorage.getItem(key) === "1") return true;
      sessionStorage.setItem(key, "1");
    } catch {}
    return false;
  };

  // Announced once, right after the reel — the point where the visitor has seen
  // what we make but hasn't yet been asked for anything. The `late` arm skips
  // this entirely and waits for the email step.
  useEffect(() => {
    if (!timerOffer || phase === "montage" || phase === "success") return;
    if (offerIntroArm === "late") return;
    if (seen("funnel_offer_intro_shown")) return;
    setOfferIntroOpen(true);
    analytics.track("Funnel Offer Intro Shown", {
      funnel: "start_brush_quiz",
      variant: offerIntroArm,
      moment: "start",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerOffer, phase, offerIntroArm]);

  // Shown again once the pack exists. The first announcement lands while there
  // is nothing to lose yet; here the clock is counting down on their own pack,
  // which is the only version of this that has any weight.
  useEffect(() => {
    if (offerIntroArm !== "test" || !offerLive || phase !== "payoff") return;
    if (seen("funnel_offer_reminder_shown")) return;
    setOfferIntroOpen(true);
    analytics.track("Funnel Offer Intro Shown", {
      funnel: "start_brush_quiz",
      variant: "test",
      moment: "payoff",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerIntroArm, offerLive, phase]);

  // Held back until the visitor has something to lose: they have answered every
  // question and watched the pack being built, and are one field away from
  // saving it. That step drops a third of the people who reach it, and this is
  // the only arm where the gift has a chance to be the reason they don't.
  const showLateIntro = useCallback(
    (stage: "quiz" | "loading" | "email") => {
      if (offerIntroArm !== "late" || stage !== "email" || !offerLive) return;
      if (seen("funnel_offer_intro_shown")) return;
      setOfferIntroOpen(true);
      analytics.track("Funnel Offer Intro Shown", {
        funnel: "start_brush_quiz",
        variant: "late",
        moment: "email",
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offerIntroArm, offerLive]
  );

  // The moment it lapses, once.
  useEffect(() => {
    if (!offerExpired || phase === "success") return;
    if (seen("funnel_offer_expired_shown")) return;
    setOfferExpiredOpen(true);
    analytics.track("Funnel Offer Expired", {
      funnel: "start_brush_quiz",
      variant: offerIntroArm,
      phase,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerExpired, phase]);
  const offerClock = useMemo(() => {
    if (offerSecondsLeft === null) return "";
    const m = Math.floor(offerSecondsLeft / 60);
    const s = offerSecondsLeft % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [offerSecondsLeft]);

  // The countdown moves money now, not time. While it runs the annual plan is
  // charged at `annual`; once it stops, at the dearer `annualFull`. Both are
  // real Stripe prices we really charge, so the struck-through figure is a
  // price rather than a claim, and letting the clock run out costs the visitor
  // something — which the free day never did.
  //
  // The free day is gone deliberately. 28 web trials produced zero conversions,
  // and the median gap between starting one and switching the renewal off was
  // 18 minutes: a brush library is emptied in one sitting, so free time is a
  // giveaway rather than a trial.
  const annualPlan = offerLive ? prices.annual : prices.annualFull || prices.annual;
  const checkoutPlan = selectedPlan === "annual" ? annualPlan : prices.weekly;
  // Derived from the two prices rather than written down beside them: a
  // hardcoded percentage goes quietly wrong the moment either price moves.
  const offerSavingsPct =
    prices.annual && prices.annualFull
      ? Math.round(100 * (1 - prices.annual.amount / prices.annualFull.amount))
      : 0;

  const handleCheckout = async ({ exitOffer = false }: { exitOffer?: boolean } = {}) => {
    // The exit downsell always targets the annual plan with the discount coupon
    // and no trial — a flat, arm-agnostic offer that doesn't touch the trial
    // experiment.
    // The exit downsell always targets the annual plan with the discount coupon
    // — a flat, arm-agnostic offer. It bills the list price and lets the coupon
    // do the discounting, so it can never come out dearer than the countdown.
    const planKey: "annual" | "weekly" = exitOffer ? "annual" : selectedPlan;
    const plan = exitOffer
      ? prices.annualFull || prices.annual
      : planKey === "annual"
      ? annualPlan
      : prices.weekly;
    if (!plan || checkoutLoading) return;
    const discounted = exitOffer;

    setCheckoutLoading(true);
    setCheckoutError("");
    analytics.track("Funnel Checkout Started", {
      funnel: "start_brush_quiz",
      plan: planKey,
      amount: plan.amount,
      is_offer_price: planKey === "annual" && offerLive,
      is_exit_offer: exitOffer,
      external_profile_id: profileId.current,
    });
    trackCheckoutStart({ plan: `funnel_${planKey}_subscription`, amount: plan.amount });
    trackStartFunnelCustomEvent("StartFunnelCheckoutStarted", {
      external_profile_id: profileId.current,
      plan: planKey,
      amount: plan.amount,
      value: plan.amount / 100,
      currency: "USD",
    });
    try {
      localStorage.setItem("last_purchase", String(plan.amount / 100));
    } catch {}

    try {
      const response = await fetch("/.netlify/functions/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sku: plan.priceId,
          ...(discounted && { applyExitOffer: true }),
          userId: profileId.current,
          email: storedEmail,
          source: "start_brush_quiz",
          variationId: `start_montage_${planKey}${
            exitOffer ? "_exitoffer" : planKey === "annual" && offerLive ? "_timeroffer" : ""
          }`,
          ...(embeddedCheckout && { embedded: true }),
        }),
      }).then((res) => res.json());

      // Embedded: the form opens over the paywall and the visitor never leaves
      // app.tools-products.com. If the secret is missing for any reason we fall
      // through to the hosted redirect rather than stranding a buyer.
      if (embeddedCheckout && response.clientSecret && stripePromise) {
        analytics.track("Funnel Checkout Created", {
          funnel: "start_brush_quiz",
          plan: planKey,
          checkout_mode: "embedded",
        });
        trackStartFunnelCustomEvent("StartFunnelCheckoutCreated", {
          external_profile_id: profileId.current,
          plan: planKey,
          amount: plan.amount,
          value: plan.amount / 100,
          currency: "USD",
        });
        setEmbeddedSecret(response.clientSecret);
        setCheckoutLoading(false);
        return;
      }

      if (response.url) {
        analytics.track("Funnel Checkout Created", {
          funnel: "start_brush_quiz",
          plan: planKey,
          checkout_mode: "hosted",
        });
        trackStartFunnelCustomEvent("StartFunnelCheckoutCreated", {
          external_profile_id: profileId.current,
          plan: planKey,
          amount: plan.amount,
          value: plan.amount / 100,
          currency: "USD",
        });
        window.location.assign(response.url);
        return;
      }
      throw new Error(response.error || "Checkout response missing url");
    } catch (error) {
      analytics.track("Funnel Checkout Failed", {
        funnel: "start_brush_quiz",
        plan: planKey,
        error_message: error instanceof Error ? error.message : String(error),
      });
      setCheckoutError("Something went wrong — please try again.");
      setCheckoutLoading(false);
    }
  };

  // Exit-intent downsell: when a paywall visitor signals they're leaving, show
  // the discount offer instead of losing them. Only while the paywall is up.
  const handleExitOffer = (source: string) => {
    setExitOfferOpen(true);
    analytics.track("Funnel Exit Offer Shown", {
      funnel: "start_brush_quiz",
      source,
      external_profile_id: profileId.current,
    });
  };
  useExitIntent(exitOfferEnabled && phase === "paywall", handleExitOffer);

  const handleExitOfferClaim = () => {
    analytics.track("Funnel Exit Offer Accepted", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
    });
    setExitOfferOpen(false);
    handleCheckout({ exitOffer: true });
  };

  const handleExitOfferDismiss = () => {
    analytics.track("Funnel Exit Offer Dismissed", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
    });
    setExitOfferOpen(false);
  };

  const handleAuthSuccess = () => {
    setAuthOpen(false);
    analytics.track("Funnel Auth Completed", {
      funnel: "start_brush_quiz",
      external_profile_id: profileId.current,
    });
    window.location.assign("/");
  };

  // Without the warm-up there are only three stops; with it the quiz owns the
  // middle of the bar so the visitor can see the questions are finite.
  const progress = withQuiz
    ? phase === "montage"
      ? 8
      : phase === "quiz"
      ? 8 + Math.round(quizProgress * 0.62)
      : phase === "payoff"
      ? 82
      : 100
    : phase === "montage"
    ? 33
    : phase === "payoff"
    ? 66
    : 100;
  const selected = selectedPlan === "annual" ? prices.annual : prices.weekly;

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      <div className="w-full px-5 pt-5 pb-3 max-w-md mx-auto">
        <div className="flex items-center justify-center mb-4">
          <span className="flex items-center gap-2">
            <img src={appIcon} alt="Tools for Procreate" className="w-7 h-7 rounded-lg" />
            <span className="font-bold text-black text-lg tracking-tight">
              Tools <span className="text-main-blue">for Procreate</span>
            </span>
          </span>
        </div>
        <div className="h-1.5 w-full bg-main-grey-300 rounded-full overflow-hidden">
          <div
            className="h-full bg-main-blue rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* The offer follows the visitor instead of appearing at the price, so
            the clock they're asked to beat is one they've actually been given.
            Not on the first screen, though. A discount above the fold answers
            "how much?" before the visitor has asked "what is this?", and the
            answer it gives is "something you pay for" — to a stranger who
            arrived from a reel and owes us nothing. The clock still starts on
            landing; it is only shown from the second screen on, once the
            product has had a chance to be worth a price. */}
        {offerLive && phase !== "montage" && phase !== "success" && (
          <div className="mt-2.5 flex items-center justify-center gap-2 bg-light-blue text-main-blue rounded-full py-1.5 px-3">
            <span className="text-xs">🎟️</span>
            <span className="text-xxs font-semibold">{offerSavingsPct}% off</span>
            <span className="text-xxs opacity-70">·</span>
            <span className="text-xxs font-mono font-bold tabular-nums">{offerClock}</span>
          </div>
        )}
        {/* The strip stays put once the clock runs out instead of vanishing.
            An offer that disappears without a trace teaches the visitor that
            the countdown never meant anything. */}
        {offerExpired && loudIntro && phase !== "success" && (
          <div className="mt-2.5 flex items-center justify-center gap-2 bg-main-grey-200 text-main-grey-800 rounded-full py-1.5 px-3">
            <span className="text-xs grayscale opacity-60">🎟️</span>
            <span className="text-xxs font-semibold line-through">{offerSavingsPct}% off</span>
            <span className="text-xxs opacity-70">·</span>
            <span className="text-xxs font-semibold">expired</span>
          </div>
        )}
      </div>

      <div className="flex-1 w-full max-w-md mx-auto px-5 pb-10 flex flex-col">
        {phase === "montage" && (
          <div className="flex-1 flex flex-col pt-4 pb-28">
            {/* Proof first, before the reel. A third of everyone leaves this
                screen without pressing anything, and a stranger's link earns
                nothing until it looks like a real product. The numbers are
                cheap to read and do that work in a second; the reel then has
                something to sell rather than something to prove. */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none">1M+</p>
                <p className="text-main-grey-800 text-xxs mt-1">artists</p>
              </div>
              <div className="w-px h-8 bg-main-grey-300" />
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none flex items-center gap-1 justify-center">
                  4.7 <span className="text-[#FFB800] text-sm">★</span>
                </p>
                <p className="text-main-grey-800 text-xxs mt-1 leading-tight">
                  16K App Store
                  <br />
                  ratings
                </p>
              </div>
              <div className="w-px h-8 bg-main-grey-300" />
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none">130+</p>
                <p className="text-main-grey-800 text-xxs mt-1">countries</p>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden border border-main-grey-300 aspect-[4/5] bg-main-grey-200">
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={SHOWCASE_POSTER_SRC}
              >
                <source src={SHOWCASE_VIDEO_SRC} type="video/mp4" />
              </video>
              <span className="absolute left-3 bottom-3 bg-white/90 text-black text-xxs font-semibold px-3 py-1.5 rounded-full">
                ● {SCENES[sceneIdx]}
              </span>
            </div>

            <h1 className="text-3xl font-bold text-black leading-tight mt-5 mb-2">
              Brushes that do <span className="text-main-blue">the hard part</span> for you
            </h1>
            <p className="text-main-grey-800 text-sm mb-4">
              Lineart, shading, color, lettering &amp; more — 10,000+ Procreate
              brushes, fonts &amp; courses in one library.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Lineart", "Shading", "Color fill", "Hair", "Anime", "Watercolor", "Lettering"].map(
                (c) => (
                  <span
                    key={c}
                    className="text-xxs font-medium text-main-grey-800 bg-main-grey-200 rounded-full px-3 py-1.5"
                  >
                    {c}
                  </span>
                )
              )}
            </div>

            {/* What the thing actually is. A stranger off a reel has seen a
                hand drawing and a headline; they still do not know whether this
                is one brush, a pack, or a shop. Brushes lead at double weight
                because that is what they came for — the rest is here so the
                price reads as covered, not to compete. */}
            <div className="mt-6">
              <p className="text-black text-xxs font-bold uppercase tracking-widest mb-3">
                What you get
              </p>
              <div className="grid grid-cols-2 gap-2">
                {WHATS_INSIDE.map((item) => (
                  <div
                    key={item.label}
                    className={`rounded-2xl px-3 py-3 ${
                      item.hero
                        ? "col-span-2 bg-light-blue border-2 border-main-blue"
                        : "bg-main-grey-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={item.hero ? "text-2xl" : "text-base"}>{item.icon}</span>
                      <span
                        className={`font-bold text-black ${item.hero ? "text-base" : "text-xs"}`}
                      >
                        {item.label}
                      </span>
                    </div>
                    <p
                      className={`text-main-grey-800 mt-1 leading-snug ${
                        item.hero ? "text-xs" : "text-xxs"
                      }`}
                    >
                      {item.detail}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* The App Store card. Not another badge — the specifics are the
                point: a named company, a version shipped days ago, a category.
                Anyone can claim a star rating; a build number from last week is
                what says somebody is still here. */}
            <div className="mt-4 rounded-2xl border-2 border-main-grey-300 px-4 py-4">
              <div className="flex items-center gap-3">
                <img
                  src={appIcon}
                  alt=""
                  className="w-11 h-11 rounded-xl shrink-0"
                />
                <div className="min-w-0">
                  <p className="text-black text-sm font-bold leading-tight">
                    Tools for Procreate
                  </p>
                  <p className="text-main-grey-800 text-xxs mt-0.5">
                    {APP_FACTS.developer}
                  </p>
                </div>
                <div className="ml-auto text-right shrink-0">
                  <p className="text-black font-bold text-base leading-none flex items-center gap-1 justify-end">
                    {APP_FACTS.rating}
                    <span className="text-[#FFB800] text-xs">★</span>
                  </p>
                  <p className="text-main-grey-800 text-xxs mt-1">
                    {APP_FACTS.ratings} ratings
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-main-grey-300 flex flex-wrap gap-x-4 gap-y-1">
                <span className="text-main-grey-800 text-xxs">
                  On the App Store since {APP_FACTS.founded} · {APP_FACTS.category}
                </span>
                <span className="text-main-grey-800 text-xxs">
                  Updated {APP_FACTS.lastUpdate} · v{APP_FACTS.version}
                </span>
              </div>
            </div>

            {/* A person, under the numbers. Verbatim from the App Store, author
                and all — at this size it is a claim about the product, so it
                has to be one somebody actually made. */}
            <div className="mt-4 bg-main-grey-200 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-[#FFB800] text-sm leading-none tracking-tight">
                  ★★★★★
                </span>
                <span className="text-black text-xxs font-semibold">
                  Mightymahree
                </span>
              </div>
              <p className="text-main-grey-800 text-xs mt-1.5 leading-snug">
                "I have spent so much money on brushes and{" "}
                <b className="text-black">
                  the amount you get with this app pays for itself
                </b>{" "}
                with the first few downloads."
              </p>
            </div>
          </div>
        )}

        {phase === "quiz" && (
          <QuizStages
            styleEmoji={styleEmoji}
            onStage={showLateIntro}
            profileId={profileId.current}
            onProgress={setQuizProgress}
            onExit={() => setPhase("montage")}
            onComplete={(quizAnswers, email) => {
              setAnswers(quizAnswers);
              setQuizEmail(email);
              setPhase("payoff");
            }}
          />
        )}

        {phase === "payoff" && (
          <div className="flex-1 flex flex-col pt-6 pb-28 text-center">
            {/* The email screen already told them who they are and what they
                matched. This screen answers the different question — what is
                actually behind the paywall — instead of restating the pack. */}
            <p className="text-main-blue text-sm font-semibold mb-2">
              {personalized
                ? `✓ Your ${pack.name} pack is saved`
                : "✓ Full library"}
            </p>
            <h2 className="text-2xl font-bold text-black leading-tight mb-2">
              And everything else{" "}
              <span className="text-main-blue">comes with it</span>
            </h2>
            <p className="text-main-grey-800 text-sm mb-4">
              10,000+ brushes across every style, plus fonts, palettes, stickers,
              3D assets and Academy courses — with new drops every week.
            </p>
            {/* The library grid, not the style image — that one already carried
                the previous screen, and repeating it makes the two read as one. */}
            <div className="rounded-2xl overflow-hidden border border-main-grey-300 mb-5">
              <img src={packGridImg} alt="" className="w-full h-32 object-cover" />
            </div>

            {personalized && (
              <div className="bg-main-grey-200 rounded-2xl px-4 py-3 mb-5 text-left flex items-start gap-3">
                <img
                  src={testimonial.image}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover shrink-0"
                />
                <div>
                  <span className="text-black text-xxs font-semibold">
                    {testimonial.name}
                  </span>
                  <p className="text-main-grey-800 text-xxs mt-0.5 leading-snug">
                    "{testimonial.text}"
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none">1M+</p>
                <p className="text-main-grey-800 text-xxs mt-1">artists</p>
              </div>
              <div className="w-px h-8 bg-main-grey-300" />
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none flex items-center gap-1 justify-center">
                  4.7 <span className="text-[#FFB800] text-sm">★</span>
                </p>
                {/* The count is the proof, not the score: a high rating carries
                    weight only once it is visibly backed by enough ratings. */}
                <p className="text-main-grey-800 text-xxs mt-1 leading-tight">
                  16K App Store
                  <br />
                  ratings
                </p>
              </div>
              <div className="w-px h-8 bg-main-grey-300" />
              <div className="text-center">
                <p className="text-black font-bold text-lg leading-none">130+</p>
                <p className="text-main-grey-800 text-xxs mt-1">countries</p>
              </div>
            </div>
          </div>
        )}

        {phase === "paywall" && (
          <div className="flex-1 flex flex-col pt-2 pb-28">
            <h2 className="text-2xl font-bold text-black leading-tight mb-1 text-center mt-2">
              Unlock the full library
            </h2>
            <p className="text-main-grey-800 text-sm text-center mb-4">
              Every style, unlimited downloads, new drops every week.
            </p>

            {offerLive && (
              /* Ticket shape: the notches and the dashed tear line are what make
                 it read as a coupon rather than a banner. */
              <div className="relative mb-5 rounded-2xl bg-main-blue text-white px-4 py-3 flex items-center gap-3 overflow-hidden">
                <span className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white" />
                <span className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white" />
                <div className="text-3xl leading-none">🎟️</div>
                <div className="flex-1 border-l border-dashed border-white/40 pl-3">
                  <p className="font-bold text-base leading-tight">
                    {offerSavingsPct}% off your first year
                  </p>
                  <p className="text-xxs opacity-90 mt-0.5">
                    {prices.annual && prices.annualFull
                      ? `${formatAmount(prices.annualFull.amount)} → ${formatAmount(
                          prices.annual.amount
                        )} — until the timer runs out`
                      : "On the annual plan — until the timer runs out"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-lg leading-none tabular-nums">
                    {offerClock}
                  </p>
                  <p className="text-xxs opacity-80 mt-0.5">left</p>
                </div>
              </div>
            )}

            {/* Big at the money moment. A person saying the thing beats a
                number saying it, and this one answers the objection the visitor
                is having right now — whether it is worth the money. Verbatim
                from the App Store listing, author and all: at this size it
                functions as a claim about the product, so it has to be one
                somebody actually made. */}
            <div className="mb-5 rounded-2xl border-2 border-main-grey-300 px-5 py-5">
              <div className="flex items-center gap-2">
                <span className="text-[#FFB800] text-lg leading-none tracking-tight">
                  ★★★★★
                </span>
              </div>
              <p className="text-black font-bold text-xl leading-tight mt-2">
                "Absolutely obsessed"
              </p>
              <p className="text-black text-sm mt-2 leading-snug">
                I have spent so much money on brushes and{" "}
                <b>the amount of brushes you get with this app pays for itself</b>{" "}
                with the first few downloads. So happy I gave it a chance.
              </p>
              <p className="text-main-grey-800 text-xxs mt-3">
                Mightymahree · App Store review
              </p>
              <div className="mt-4 pt-4 border-t border-main-grey-300 flex items-center justify-center gap-4">
                <div className="text-center">
                  <p className="text-black font-bold text-base leading-none">1M+</p>
                  <p className="text-main-grey-800 text-xxs mt-1">artists</p>
                </div>
                <div className="w-px h-7 bg-main-grey-300" />
                <div className="text-center">
                  <p className="text-black font-bold text-base leading-none flex items-center gap-1 justify-center">
                    4.7 <span className="text-[#FFB800] text-xs">★</span>
                  </p>
                  <p className="text-main-grey-800 text-xxs mt-1">16K ratings</p>
                </div>
                <div className="w-px h-7 bg-main-grey-300" />
                <div className="text-center">
                  <p className="text-black font-bold text-base leading-none">130+</p>
                  <p className="text-main-grey-800 text-xxs mt-1">countries</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setSelectedPlan("annual")}
                className={`w-full rounded-2xl border-2 px-4 py-4 text-left transition-colors ${
                  selectedPlan === "annual"
                    ? "border-main-blue bg-light-blue"
                    : "border-main-grey-300 bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-black text-sm font-bold">Annual</span>
                    {/* While the countdown runs the badge names the discount it
                        actually grants; otherwise it falls back to the saving
                        against paying weekly for a year. Both are real numbers
                        we really charge. */}
                    {offerLive && offerSavingsPct > 0 ? (
                      <span className="ml-2 bg-main-blue text-white text-xxs font-bold px-2 py-0.5 rounded-full">
                        SAVE {offerSavingsPct}%
                      </span>
                    ) : (
                      annualSavings && (
                        <span className="ml-2 bg-main-blue text-white text-xxs font-bold px-2 py-0.5 rounded-full">
                          SAVE {annualSavings}%
                        </span>
                      )
                    )}
                    <p className="text-main-grey-800 text-xxs mt-1">
                      {annualPlan
                        ? `${formatAmount(annualPlan.amount / 12)} / month, billed annually`
                        : "Best value"}
                    </p>
                  </div>
                  {/* Per-day framing leads, full price stays visible underneath:
                      a daily number reads as a categorically smaller commitment
                      than the same amount billed yearly. */}
                  <div className="text-right shrink-0 pl-3">
                    <span className="text-black font-bold text-3xl leading-none block tracking-tight">
                      {annualPlan ? formatAmount(annualPlan.amount / 365) : "—"}
                    </span>
                    <span className="text-main-grey-800 text-xxs">per day</span>
                    {/* Whatever is struck through here is a price we really
                        charge: the list annual while the coupon is running, and
                        otherwise what a year on the weekly plan costs. Neither
                        is an invented "was" figure. */}
                    {annualPlan && (
                      <span className="text-main-grey-800 text-xxs block mt-0.5">
                        {offerLive && prices.annualFull ? (
                          <span className="line-through opacity-70">
                            {formatAmount(prices.annualFull.amount)}{" "}
                          </span>
                        ) : (
                          prices.weekly && (
                            <span className="line-through opacity-70">
                              {formatAmount(prices.weekly.amount * 52)}{" "}
                            </span>
                          )
                        )}
                        <span className="text-main-blue font-semibold">
                          {formatAmount(annualPlan.amount)}/yr
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </button>

              <button
                onClick={() => setSelectedPlan("weekly")}
                className={`w-full rounded-2xl border-2 px-4 py-4 text-left transition-colors ${
                  selectedPlan === "weekly"
                    ? "border-main-blue bg-light-blue"
                    : "border-main-grey-300 bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-black text-sm font-bold">Weekly</span>
                    <p className="text-main-grey-800 text-xxs mt-1">Cancel anytime</p>
                  </div>
                  <div className="text-right shrink-0 pl-3">
                    <span className="text-black font-bold text-3xl leading-none block tracking-tight">
                      {prices.weekly ? formatAmount(prices.weekly.amount / 7) : "—"}
                    </span>
                    <span className="text-main-grey-800 text-xxs">per day</span>
                    {prices.weekly && (
                      <span className="text-main-grey-800 text-xxs block mt-0.5">
                        {formatAmount(prices.weekly.amount)}/wk
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-4 space-y-2 text-sm text-black">
              <p>✅ 10,000+ brushes across every style</p>
              <p>✅ Unlimited premium downloads</p>
              <p>✅ Academy courses &amp; workflow guides</p>
              <p>✅ New drops every week</p>
            </div>

            <div className="mt-5 border-2 border-main-grey-300 rounded-2xl px-4 py-4 flex items-start gap-3">
              <span className="text-2xl">🛡️</span>
              <div>
                <p className="text-black text-sm font-bold">30-day money-back guarantee</p>
                <p className="text-main-grey-800 text-xxs mt-1 leading-snug">
                  If you're not happy, get a full refund within 30 days — no
                  questions asked.
                </p>
              </div>
            </div>

            <h3 className="text-black font-bold text-lg mt-6 mb-3">What artists say</h3>
            <div className="space-y-3">
              {REVIEWS.map((review) => (
                <div key={review.name} className="bg-main-grey-200 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <img
                      src={review.image}
                      alt={`${review.name} review`}
                      className="w-7 h-7 rounded-full object-cover shrink-0"
                    />
                    <div>
                      <span className="text-black text-xxs font-semibold block leading-none">
                        {review.name}
                      </span>
                      <span className="text-main-grey-800 text-xxs">
                        {review.detail} · Verified purchase
                      </span>
                    </div>
                  </div>
                  <p className="text-main-grey-800 text-xxs leading-snug">"{review.text}"</p>
                </div>
              ))}
            </div>

            {checkoutError && (
              <p className="text-main-red-900 text-xxs text-center mt-3">{checkoutError}</p>
            )}
          </div>
        )}

        {phase === "success" && (
          <div className="flex-1 flex flex-col justify-center text-center">
            <div className="w-16 h-16 rounded-full bg-light-blue text-main-blue flex items-center justify-center text-3xl mx-auto mb-5">
              🎉
            </div>
            <h2 className="text-2xl font-bold text-black leading-tight mb-2">
              Payment confirmed — your library is unlocked
            </h2>
            <p className="text-main-grey-800 text-sm mb-6">
              Sign in with{" "}
              <b className="text-black">{storedEmail || "the email you used at checkout"}</b>{" "}
              to open your library. Use the same email in the iOS app to unlock
              premium there too.
            </p>
            <button
              onClick={() => {
                analytics.track("Funnel Sign In Opened", { funnel: "start_brush_quiz" });
                setAuthOpen(true);
              }}
              className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform"
            >
              Sign in to unlock
            </button>
            <a
              href="/"
              className="block w-full border-2 border-main-grey-300 text-black font-semibold py-4 rounded-2xl text-lg mt-3"
            >
              Browse the library
            </a>
          </div>
        )}
      </div>

      {/* Sticky CTA per phase */}
      {phase === "montage" && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-main-grey-300 px-5 pt-3 pb-6">
          <div className="max-w-md mx-auto">
            <button
              onClick={() => setPhase(withQuiz ? "quiz" : "payoff")}
              className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform"
            >
              {withQuiz ? "Show me which brushes are mine" : "Get the brushes"}
            </button>
            {/* One line under the button, because the button now promises a
                result rather than a search, and the obvious next question is
                what it costs to find out. Naming the effort — not the price —
                is what keeps them moving. */}
            {/* No question count here on purpose. The quiz branches — the
                spend question only appears for people who have bought brushes —
                so any fixed number is wrong for somebody. Time is the honest
                unit and the one being weighed anyway: median landing-to-price
                is 1.5 minutes. */}
            <p className="text-main-grey-800 text-xxs text-center mt-2">
              Takes about a minute · no card needed
            </p>
          </div>
        </div>
      )}
      {phase === "payoff" && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-main-grey-300 px-5 pt-3 pb-6">
          <div className="max-w-md mx-auto">
            <button
              onClick={() => setPhase("paywall")}
              className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform"
            >
              Unlock the library
            </button>
          </div>
        </div>
      )}
      {phase === "paywall" && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-main-grey-300 px-5 pt-3 pb-4">
          <div className="max-w-md mx-auto">
            <button
              onClick={() => handleCheckout()}
              disabled={checkoutLoading || !selected}
              className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform disabled:opacity-70"
            >
              {checkoutLoading
                ? "Opening secure checkout…"
                : selectedPlan === "annual" && offerLive && offerSavingsPct > 0
                ? `Claim ${offerSavingsPct}% off`
                : "Unlock the library"}
            </button>
            <p className="text-main-grey-800 text-xxs text-center mt-2">
              {selectedPlan === "annual" && offerLive && prices.annual && prices.annualFull
                ? `${formatAmount(prices.annualFull.amount)} ${formatAmount(
                    prices.annual.amount
                  )}/year while the timer runs · cancel anytime`
                : "🔒 Secure payment via Stripe · Cancel anytime · 30-day guarantee"}
            </p>
          </div>
        </div>
      )}

      {/* Stripe's own form, on our page. The sheet slides over the paywall so
          the plan the visitor just chose stays behind it rather than being
          replaced by a different site. Closing it is possible on purpose:
          trapping someone in a payment form is how you earn a chargeback. */}
      {embeddedSecret && stripePromise && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center">
          <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto">
            {/* Stripe draws its own summary below and it leads with the yearly
                total. This header is the one part of the sheet we own, so it
                carries the framing the visitor already agreed to on the
                paywall: the discount first, then the daily figure, with the
                real billed amount beside it rather than hidden behind it. */}
            <div className="sticky top-0 bg-white flex items-start justify-between gap-3 px-5 py-4 border-b border-main-grey-300">
              <div>
                {selectedPlan === "annual" && offerLive && offerSavingsPct > 0 && (
                  <p className="text-main-blue text-base font-bold leading-tight">
                    {offerSavingsPct}% off applied
                  </p>
                )}
                {checkoutPlan ? (
                  <p className="text-black text-sm mt-0.5">
                    <b>{formatAmount(checkoutPlan.amount / (selectedPlan === "annual" ? 365 : 7))}</b>{" "}
                    per day
                    <span className="text-main-grey-800">
                      {" "}
                      · billed {formatAmount(checkoutPlan.amount)}/
                      {selectedPlan === "annual" ? "year" : "week"}
                    </span>
                  </p>
                ) : (
                  <span className="text-black font-bold text-base">Secure checkout</span>
                )}
              </div>
              <button
                onClick={() => {
                  analytics.track("Funnel Checkout Dismissed", {
                    funnel: "start_brush_quiz",
                    checkout_mode: "embedded",
                  });
                  setEmbeddedSecret(null);
                }}
                aria-label="Close"
                className="text-main-grey-800 text-2xl leading-none px-2"
              >
                ×
              </button>
            </div>
            <div className="px-2 pb-4">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ clientSecret: embeddedSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        </div>
      )}

      {offerIntroOpen && offerLive && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-5"
          role="dialog"
          aria-modal="true"
          onClick={loudIntro ? undefined : () => setOfferIntroOpen(false)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-3xl p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {loudIntro ? (
              <>
                {/* The clock is the subject here, not a closing footnote. What
                    is being lost has to be legible before what is being
                    offered, or the offer reads as an advert. */}
                <p className="text-main-grey-800 text-xxs font-bold uppercase tracking-widest">
                  Your {offerSavingsPct}% off is running
                </p>
                <p className="text-black font-mono font-bold tabular-nums text-6xl leading-none my-3 tracking-tight">
                  {offerClock}
                </p>
                <h2 className="text-2xl font-bold text-black leading-tight mb-2">
                  A year of everything for{" "}
                  <span className="text-main-blue">
                    {prices.annual ? formatAmount(prices.annual.amount) : "less"}
                  </span>
                </h2>
                <p className="text-main-grey-800 text-sm mb-3">
                  All 10,000+ brushes, fonts and courses.
                  {prices.annualFull
                    ? ` Normally ${formatAmount(prices.annualFull.amount)} a year.`
                    : ""}
                </p>
                <p className="text-black text-sm font-semibold mb-5">
                  When this hits zero, the price goes back up.
                </p>
                <button
                  onClick={() => setOfferIntroOpen(false)}
                  className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform"
                >
                  Claim {offerSavingsPct}% off
                </button>
                {/* The discounted figure is named here, unlike the free-day
                    version this replaced. A gift could be announced without a
                    price because the price was the bad news; a discount IS the
                    price, and hiding it would make the paywall the moment the
                    visitor discovers there is one. */}
                <p className="text-main-grey-800 text-xxs mt-3">Cancel anytime</p>
              </>
            ) : (
              <>
                <div className="text-5xl mb-3">🎟️</div>
                <p className="text-main-blue text-xxs font-bold uppercase tracking-widest">
                  Unlocked for you
                </p>
                <h2 className="text-3xl font-bold text-black leading-tight mt-2 mb-3">
                  <span className="text-main-blue">{offerSavingsPct}% off</span> your first year
                </h2>
                <p className="text-main-grey-800 text-sm mb-1">
                  All 10,000+ brushes, fonts and courses
                  {prices.annual && prices.annualFull ? (
                    <>
                      {" "}
                      — <span className="line-through">{formatAmount(prices.annualFull.amount)}</span>{" "}
                      <b>{formatAmount(prices.annual.amount)}</b> for the year
                    </>
                  ) : (
                    ""
                  )}
                </p>
                {/* The clock is the deadline to claim, not the length of the
                    discount — conflating the two would promise a year at this
                    price and hand over a quarter of an hour. */}
                <p className="text-black text-sm font-semibold mb-5">
                  Claim it within{" "}
                  <span className="font-mono tabular-nums text-main-blue">{offerClock}</span>
                </p>
                <button
                  onClick={() => setOfferIntroOpen(false)}
                  className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform"
                >
                  Let's go
                </button>
                {prices.annualFull && (
                  <p className="text-main-grey-800 text-xxs mt-3">
                    Then {formatAmount(prices.annualFull.amount)}/year · cancel anytime
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* What expiry looks like. Shown once, and only in the louder arm: the
          quiet arm's offer vanishes the way it always did, which is the thing
          being tested. */}
      {offerExpiredOpen && loudIntro && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-5"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm bg-white rounded-3xl p-6 text-center shadow-2xl">
            <div className="text-5xl mb-3 grayscale opacity-50">🎟️</div>
            <p className="text-main-grey-800 text-xxs font-bold uppercase tracking-widest">
              Expired
            </p>
            <p className="text-main-grey-800 font-mono font-bold tabular-nums text-5xl leading-none my-3 line-through opacity-50">
              00:00
            </p>
            <h2 className="text-2xl font-bold text-black leading-tight mb-2">
              Your {offerSavingsPct}% off is gone
            </h2>
            <p className="text-main-grey-800 text-sm mb-5">
              The library is still here — the annual plan is back to{" "}
              {prices.annualFull ? formatAmount(prices.annualFull.amount) : "full price"} a year.
            </p>
            <button
              onClick={() => setOfferExpiredOpen(false)}
              className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform"
            >
              See the plans
            </button>
          </div>
        </div>
      )}

      <ExitOfferModal
        open={exitOfferOpen}
        annualAmount={prices.annual?.amount}
        onClaim={handleExitOfferClaim}
        onDismiss={handleExitOfferDismiss}
      />

      {phase === "success" && (
        <UserContext.Provider value={{ user: null }}>
          <SignIn
            isOpen={isAuthOpen}
            onClose={() => setAuthOpen(false)}
            onSuccess={handleAuthSuccess}
            initialEmail={storedEmail}
            isEmailLocked={Boolean(storedEmail)}
            hideGoogle
          />
        </UserContext.Provider>
      )}
    </div>
  );
};
